import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BadgeNotification, Tabs } from '@Sage/hoi-poi-ui';
import { arraysIsEqual as isEqualArray, isEqual } from '@Sage/ssm-utils';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';

import Conversations from 'components/SvgIcons/emptyScreen/Conversations';
import Detail from 'components/SvgIcons/emptyScreen/Detail';
import { ACTIVITIES } from 'constants/Entities';
import {
    ACTIVITY_DETAIL_TABS,
    getIsActivityTypeWithExtraFields,
    getIsActivityTypeWithFilesTab,
    getIsActivityTypeWithMaps,
} from 'containers/Activities/utils/activities';
import CustomEmptyView from 'containers/components/CustomEmptyView';
import DetailFiles from 'containers/components/EntityDetail/components/DetailFiles';
import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import {
    ACTIVITY_TYPE_ANNOTATION,
    ACTIVITY_TYPE_CALL,
    ACTIVITY_TYPE_CHECKIN,
    ACTIVITY_TYPE_EMAIL,
    ACTIVITY_TYPE_FAST_CHECKIN,
    ACTIVITY_TYPE_MARKETING_CAMPAIGN,
    ACTIVITY_TYPE_OPPORTUNITY_CHECKIN,
    ACTIVITY_TYPE_WHATSAPP,
} from 'models/ActivityModel';
import { ActivitiesService } from 'services';
import { getActivityDateFormat, getElapsedTime, getMomentFromDateBackend } from 'utils/dates';
import {
    getLiteral,
    getLiteralWithParameters,
    getLiteralWithParametersHtml,
} from 'utils/getLiteral';
import { errorToast } from 'utils/toast';
import { logEvent } from 'utils/tracking';

import useActivitiesDetail from '../../hooks/useActivitiesDetail';
import useActivitiesDetailState from '../../hooks/useActivitiesDetailState';
import { inConversations } from '../../utils/conversations';
import DetailLoader from '../Loaders/DetailLoader';
import GeneralDetail from './GeneralDetail';
import DetailDocument from './tabs/Attachments';
import DetailEmail from './tabs/Email';
import DetailExtraFields from './tabs/ExtraFields';
import DetailMap from './tabs/Map';
import DetailMarketingCampaign from './tabs/MarketingCampaign';
import DetailMarketingCampaignInteractions from './tabs/MarketingCampaignInteractions';
import DetailMessages from './tabs/Messages';
import DetailTotalLinks from './tabs/TotalLinks';
import DetailTotalViews from './tabs/TotalViews';
import DetailWhatsapp from './tabs/Whatsapp';
import './styles.scss';

const ActivitiesDetail = memo(
    ({
        detailWidth,
        fromWidgetInitialTab,
        onChangeTabForWidget,
        unreadMessages = {},
        widgetActivity,
    }) => {
        const {
            activity,
            activities,
            allowEmailAttachments,
            canCreateDocuments,
            canDeleteImages,
            canEditActivity,
            canUploadImages,
            canViewAllGeopositionedActivities,
            enableTimeLine,
            extraFields,
            extraFieldsLoading,
            filesList,
            getActivityFiles,
            initialTabItem,
            isLoadingFilesList,
            loading,
            matchingName,
            removeActivityFile,
            showActionsAndMenu,
            tab,
            tabsNames,
            updateDetailExtra,
            updateEntityDetail,
            updateTabsNames,
        } = useActivitiesDetailState(widgetActivity);

        // --- Context & hooks
        const location = useLocation();
        const { getHeaderItems } = useActivitiesDetail();
        const { hasTabs, openTab } = useEntityDetail();

        // --- State
        const [emailWithImages, setEmailWithImages] = useState('');
        const [forceTotalMessages, setForceTotalMessages] = useState(null);
        const [isCommentCollapsed, setIsCommentCollapsed] = useState(true);
        const [loadingEmailWithImages, setLoadingEmailWithImages] = useState(false);
        const [scrollToMessage, setScrollToMessage] = useState(true);
        const domDetailInfo = useRef(null);
        const domDetailTabs = useRef(null);
        const filesListLengthRef = useRef(filesList?.length || 0);
        const newMessagesRef = useRef({});
        const prevActivity = useRef(null);
        const previousLocation = useRef(null);
        const prevTab = useRef(null);
        const shouldOverrideInitialTab = useRef(true);
        const unreadMessagesRef = useRef({});
        const isFromWidgetDetail = !!widgetActivity;

        // --- Handlers
        const setTab = useCallback(
            (tab) => {
                if (!activity) return;
                updateDetailExtra(ACTIVITIES, activity.Id, { tab });
            },
            [activity, updateDetailExtra],
        );

        // --- Effects
        useEffect(() => {
            // Override initial tab if coming from widget with fromWidgetInitialTab
            if (shouldOverrideInitialTab.current && isFromWidgetDetail && fromWidgetInitialTab) {
                setTab(fromWidgetInitialTab);
                shouldOverrideInitialTab.current = false;
            }
        }, [fromWidgetInitialTab, isFromWidgetDetail, setTab]);

        // --- Derived state
        const newMessageUID = useMemo(() => {
            if (!activity) return;
            const { Id, ActivityType } = activity;
            return initialTabItem
                ? `${Id}${ActivityType}${initialTabItem}`
                : `${Id}${ActivityType}`;
        }, [activity, initialTabItem]);

        const handleNewMessage = useCallback(
            (value) => {
                if (!newMessageUID) return;
                newMessagesRef.current = { ...newMessagesRef.current, [newMessageUID]: value };
            },
            [newMessageUID],
        );

        const handleUnreadMessages = useCallback(() => {
            if (!activity) return;
            const { ActivityType, Id, TimeLineUnreadMessages } = activity;
            // Because BE: For the unread messages endpoint everything that is not a phone call or email
            // is an "activity"
            const type = [ACTIVITY_TYPE_CALL, ACTIVITY_TYPE_EMAIL].includes(ActivityType)
                ? ActivityType
                : ACTIVITY_TYPE_ANNOTATION;
            const updatedActivitiesObj = unreadMessages?.[type] || {};
            const updatedActivitiesIds = Object.keys(updatedActivitiesObj);
            if (
                updatedActivitiesIds.includes(Id) &&
                parseInt(TimeLineUnreadMessages, 10) !== updatedActivitiesObj[Id]
            ) {
                updateEntityDetail(ACTIVITIES, Id, {
                    TimeLineUnreadMessages: updatedActivitiesObj[Id],
                });
            }
        }, [activity, unreadMessages, updateEntityDetail]);

        useEffect(() => {
            if (location !== previousLocation.current) {
                previousLocation.current = location;
                setScrollToMessage(true);
            }
        }, [location]);

        useEffect(() => {
            if (isEqual(unreadMessages, unreadMessagesRef.current)) return;
            unreadMessagesRef.current = unreadMessages;
            if (Object.entries(unreadMessages).length > 0) {
                handleUnreadMessages();
            }
        }, [handleUnreadMessages, unreadMessages]);

        const finalWidth = useMemo(
            () => (isFromWidgetDetail ? '100%' : detailWidth),
            [detailWidth, isFromWidgetDetail],
        );

        const getEmailWithImages = useCallback(() => {
            setLoadingEmailWithImages(true);
            ActivitiesService.getEmailWithImages(activity.Id)
                .then((result) => {
                    if (result?.bodyFull) setEmailWithImages(result?.bodyFull);
                    setLoadingEmailWithImages(false);
                })
                .catch((error) => {
                    if (error?.code === 400 && error.error) {
                        logEvent({
                            event: ACTIVITIES.trueName,
                            submodule: 'email',
                            functionality: 'errorDownloadImages',
                        });
                        errorToast({
                            text: getLiteral(error.error),
                        });
                    }
                    setLoadingEmailWithImages(false);
                });
        }, [activity]);

        const isActivityTypeWithExtraFields = useMemo(() => {
            if (!activity) return false;
            return getIsActivityTypeWithExtraFields(activity.ActivityType);
        }, [activity]);

        const isActivityTypeWithMaps = useMemo(() => {
            if (!activity) return false;
            return getIsActivityTypeWithMaps(activity.ActivityType);
        }, [activity]);

        const isActivityTypeWithFilesTab = useMemo(() => {
            if (!activity) return false;
            return getIsActivityTypeWithFilesTab(activity.ActivityType);
        }, [activity]);

        const hasCoordinates = useMemo(() => {
            return (
                activity?.Latitude &&
                activity?.Longitude &&
                activity?.Latitude !== '-1' &&
                activity?.Longitude !== '-1' &&
                activity?.Latitude !== '0' &&
                activity?.Longitude !== '0'
            );
        }, [activity]);

        const hasExtraFields = useMemo(() => {
            if (extraFieldsLoading) return;
            const listExtraFields = extraFields.filter((current) => {
                return current.list.length > 0;
            });
            return listExtraFields.length > 0;
        }, [extraFields, extraFieldsLoading]);

        const isCheckIn = useMemo(() => {
            if (!activity) return false;
            return (
                activity.ActivityType === ACTIVITY_TYPE_CHECKIN ||
                activity.ActivityType === ACTIVITY_TYPE_OPPORTUNITY_CHECKIN ||
                activity.ActivityType === ACTIVITY_TYPE_FAST_CHECKIN
            );
        }, [activity]);

        useEffect(() => {
            if (!isLoadingFilesList) {
                filesListLengthRef.current = filesList?.length || 0;
            }
        }, [filesList, isLoadingFilesList]);

        useEffect(() => {
            if (extraFieldsLoading || !activity) return;

            const previousActivity = prevActivity.current;
            const hasActivityChanged =
                activity.Id !== previousActivity?.Id ||
                activity.ActivityType !== previousActivity?.ActivityType;

            if (hasActivityChanged || tab !== prevTab.current) {
                prevActivity.current = activity;
                const canShowMapTab =
                    isActivityTypeWithMaps &&
                    (isCheckIn || canViewAllGeopositionedActivities) &&
                    hasCoordinates;
                const isMarketingCampaign =
                    activity?.ActivityType === ACTIVITY_TYPE_MARKETING_CAMPAIGN;
                const marketingCampaignTabKeys = [
                    ACTIVITY_DETAIL_TABS.INFORMATION,
                    ...(isFromWidgetDetail ? [ACTIVITY_DETAIL_TABS.INTERACTIONS] : []),
                    ACTIVITY_DETAIL_TABS.TIMELINE,
                ];
                const hasValidCurrentTab =
                    tab &&
                    (tab !== ACTIVITY_DETAIL_TABS.MAP || canShowMapTab) &&
                    (!isMarketingCampaign || marketingCampaignTabKeys.includes(tab));
                let newTab = hasValidCurrentTab ? tab : null;

                if (isActivityTypeWithFilesTab) {
                    getActivityFiles(activity?.Id);
                }

                if (emailWithImages) {
                    setEmailWithImages('');
                }

                if (!newTab) {
                    switch (true) {
                        case canShowMapTab:
                            newTab = ACTIVITY_DETAIL_TABS.MAP;
                            break;
                        case activity?.ActivityType === ACTIVITY_TYPE_MARKETING_CAMPAIGN:
                            newTab = ACTIVITY_DETAIL_TABS.INFORMATION;
                            break;
                        case activity?.ActivityType === ACTIVITY_TYPE_WHATSAPP &&
                            activity?.WhatsappMessage &&
                            activity?.WhatsappMessage !== '':
                            newTab = ACTIVITY_DETAIL_TABS.WHATSAPP;
                            break;
                        case isActivityTypeWithExtraFields && hasExtraFields:
                            newTab = ACTIVITY_DETAIL_TABS.EXTRA_INFO;
                            break;
                        case allowEmailAttachments && activity?.attachments?.length:
                            newTab = ACTIVITY_DETAIL_TABS.ATTACHMENTS;
                            break;
                        case isActivityTypeWithFilesTab:
                            newTab = ACTIVITY_DETAIL_TABS.FILES;
                            break;
                        case activity?.ActivityType === ACTIVITY_TYPE_CALL:
                            newTab = ACTIVITY_DETAIL_TABS.TIMELINE;
                            break;
                        case activity?.ActivityType === ACTIVITY_TYPE_EMAIL:
                            newTab = ACTIVITY_DETAIL_TABS.EMAIL;
                            break;
                        default:
                            break;
                    }
                }

                if (newTab && newTab !== tab) {
                    setTab(newTab);
                }

                prevTab.current = newTab || null;
            }
        }, [
            activity,
            allowEmailAttachments,
            emailWithImages,
            enableTimeLine,
            extraFieldsLoading,
            getActivityFiles,
            hasCoordinates,
            hasExtraFields,
            isActivityTypeWithExtraFields,
            isActivityTypeWithFilesTab,
            isActivityTypeWithMaps,
            isCheckIn,
            isFromWidgetDetail,
            canViewAllGeopositionedActivities,
            tab,
            setTab,
        ]);

        const labels = useMemo(() => {
            return {
                account: getLiteral('label_activity_account_field'),
                attachment: getLiteral('label_attachments'),
                by: getLiteral('label_search_by'),
                campaign: getLiteral('label_campaign'),
                contact: getLiteral('label_contacts'),
                duration: getLiteral('label_duration'),
                emailStatus: getLiteral('label_email_status'),
                from: getLiteral('label_from'),
                in: getLiteral('common_in'),
                opportunity: getLiteral('title_opportunity'),
                role: getLiteral('label_cargoempresa'),
                subject: getLiteral('label_activity_subject_field'),
                to: getLiteral('label_to'),
                with: getLiteral('common_with'),
            };
        }, []);

        const getDateText = useCallback((date) => {
            if (!date) return '';
            const dateMoment = getMomentFromDateBackend(date, null, true);
            return getActivityDateFormat(dateMoment, true);
        }, []);

        const getElapsedDateText = useCallback((date) => {
            if (!date) return '';
            const elapsedTime = getElapsedTime(getMomentFromDateBackend(date, null, true));
            return elapsedTime;
        }, []);

        const getIsCommentCollapsed = useCallback(() => {
            setIsCommentCollapsed(!isCommentCollapsed);
        }, [isCommentCollapsed]);

        // --- Memoized props
        const generalDetailProps = useMemo(
            () => ({
                activity,
                canEditActivity,
                emailWithImages,
                getDateText,
                getElapsedDateText,
                getEmailWithImages,
                getHeaderItems,
                getIsCommentCollapsed,
                hasTabs,
                isFromWidgetDetail,
                labels,
                loadingEmailWithImages,
                openTab,
                showActionsAndMenu,
            }),
            [
                activity,
                canEditActivity,
                emailWithImages,
                getDateText,
                getElapsedDateText,
                getEmailWithImages,
                getHeaderItems,
                getIsCommentCollapsed,
                hasTabs,
                isFromWidgetDetail,
                labels,
                loadingEmailWithImages,
                openTab,
                showActionsAndMenu,
            ],
        );

        const getConversationTabTitle = useCallback(() => {
            const totalMessages = parseInt(activity?.timeLineMessages, 10);
            const unreadMessages = parseInt(activity?.TimeLineUnreadMessages, 10);

            return (
                <div id="activities-detail-timeline-tab-title">
                    {totalMessages === 0
                        ? getLiteral('label_conversation')
                        : getLiteralWithParametersHtml(
                              `${getLiteral('label_conversation')} %@`,
                              [unreadMessages],
                              (num) =>
                                  forceTotalMessages > 0 || num > 0 ? (
                                      <BadgeNotification size="tiny" className="badge--unread">
                                          {forceTotalMessages || num}
                                      </BadgeNotification>
                                  ) : null,
                          )}
                </div>
            );
        }, [activity?.timeLineMessages, activity?.TimeLineUnreadMessages, forceTotalMessages]);

        const tabs = useMemo(() => {
            let finalTabs = [];

            if (!activity?.ActivityType) return finalTabs;

            if (activity.ActivityType === ACTIVITY_TYPE_MARKETING_CAMPAIGN) {
                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.INFORMATION,
                    title: getLiteral('label_information'),
                });

                if (isFromWidgetDetail) {
                    finalTabs.push({
                        key: ACTIVITY_DETAIL_TABS.INTERACTIONS,
                        title: getLiteral('label_interactions'),
                    });
                }

                if (enableTimeLine) {
                    finalTabs.push({
                        key: ACTIVITY_DETAIL_TABS.TIMELINE,
                        title: getConversationTabTitle(),
                    });
                }

                return finalTabs;
            }

            if (
                activity &&
                isActivityTypeWithMaps &&
                hasCoordinates &&
                (isCheckIn || canViewAllGeopositionedActivities)
            ) {
                finalTabs.push({ key: ACTIVITY_DETAIL_TABS.MAP, title: getLiteral('label_map') });
            }

            if (activity && isActivityTypeWithExtraFields && hasExtraFields) {
                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.EXTRA_INFO,
                    title: getLiteral('label_extra_info'),
                });
            }

            if (
                allowEmailAttachments &&
                activity &&
                activity?.attachments?.length &&
                activity.ActivityType !== ACTIVITY_TYPE_EMAIL
            ) {
                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.ATTACHMENTS,
                    title: getLiteral('title_documents'),
                });
            }

            if (activity && isActivityTypeWithFilesTab) {
                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.FILES,
                    title: (
                        <div id="activities-detail-images-tab-title">
                            {isLoadingFilesList
                                ? getLiteralWithParameters('label_files_with_total', [
                                      filesListLengthRef.current,
                                  ])
                                : getLiteralWithParameters('label_files_with_total', [
                                      filesList?.length,
                                  ])}
                        </div>
                    ),
                });
            }

            if (activity && activity.ActivityType === ACTIVITY_TYPE_EMAIL) {
                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.EMAIL,
                    title: getLiteral('label_Email'),
                });
            }

            if (activity?.WhatsappMessage && activity.ActivityType === ACTIVITY_TYPE_WHATSAPP) {
                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.WHATSAPP,
                    title: getLiteral('label_whatsapp_message'),
                });
            }

            if (activity && enableTimeLine) {
                const totalMessages = parseInt(activity.timeLineMessages, 10);
                const unreadMessages = parseInt(activity.TimeLineUnreadMessages, 10);

                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.TIMELINE,
                    title: (
                        <div id="activities-detail-timeline-tab-title">
                            {totalMessages === 0
                                ? getLiteral('label_timeline_tab_title_no_comments')
                                : getLiteralWithParametersHtml(
                                      `${getLiteral('label_timeline_tab_title')} %@`,
                                      [unreadMessages],
                                      (num) =>
                                          forceTotalMessages > 0 || num > 0 ? (
                                              <BadgeNotification
                                                  size="tiny"
                                                  className="badge--unread"
                                              >
                                                  {forceTotalMessages || num}
                                              </BadgeNotification>
                                          ) : null,
                                  )}
                        </div>
                    ),
                });
            }

            // Total views && total link clicks
            if (activity?.ActivityType === ACTIVITY_TYPE_EMAIL && activity?.Tracking) {
                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.TOTAL_VIEWS,
                    title: <div className="fm-tabs__new">{getLiteral('label_total_views')}</div>,
                });

                finalTabs.push({
                    key: ACTIVITY_DETAIL_TABS.TOTAL_LINKS,
                    title: <div className="fm-tabs__new">{getLiteral('label_links_opened')}</div>,
                });
            }

            return finalTabs;
        }, [
            activity,
            isActivityTypeWithMaps,
            hasCoordinates,
            isCheckIn,
            canViewAllGeopositionedActivities,
            isActivityTypeWithExtraFields,
            isActivityTypeWithFilesTab,
            hasExtraFields,
            allowEmailAttachments,
            enableTimeLine,
            isFromWidgetDetail,
            isLoadingFilesList,
            filesList?.length,
            forceTotalMessages,
            getConversationTabTitle,
        ]);

        const computedTabsNames = useMemo(() => tabs.map((tab) => tab.key), [tabs]);

        useEffect(() => {
            if (!isEqualArray(tabsNames, computedTabsNames)) {
                updateTabsNames(computedTabsNames);
            }
        }, [tabsNames, computedTabsNames, updateTabsNames]);

        const noDetailProps = useCallback(() => {
            return inConversations(location)
                ? {
                      title: getLiteral('label_empty_screen_my_conversations_detail'),
                      subtitle: getLiteral('label_empty_screen_my_conversations_detail_desc'),
                      icon: <Conversations />,
                      overrides: { title: { type: 'h6' }, description: { type: 'subtitle1' } },
                  }
                : {
                      title: getLiteral('label_no_activity_selected'),
                      subtitle: getLiteral('label_no_activity_selected_desc'),
                      icon: <Detail />,
                      overrides: { title: { type: 'h6' }, description: { type: 'subtitle1' } },
                  };
        }, [location]);

        const forceLoader = useMemo(() => {
            if (!isFromWidgetDetail && activity && !activities.length) {
                return false;
            } else if (!isFromWidgetDetail && !activities.length) {
                return true;
            } else {
                return false;
            }
        }, [activities, isFromWidgetDetail, activity]);

        const defaultLoaderWidth = useMemo(() => {
            const body = document.querySelector('body');
            const bodyWidth = body.offsetWidth;
            const navigationEl = document.querySelector('.react-navigation-layout');
            const navigationWidth = navigationEl?.offsetWidth || 0;
            const listEl = document.querySelector('.fm-activities-list');
            const listWidth = listEl?.offsetWidth || 0;
            const staticNavigationWidth = 224;
            const staticListWidth = (bodyWidth - navigationWidth) * 0.5;

            if (navigationWidth && listWidth) return bodyWidth - navigationWidth - listWidth;
            return bodyWidth - staticNavigationWidth - staticListWidth;
        }, []);

        const defaultLoaderHeight = useMemo(() => {
            const body = document.querySelector('body');
            const bodyHeight = body.offsetHeight;
            const headersHeight = 64;
            return bodyHeight - headersHeight * 2;
        }, []);

        const onChangeTab = useCallback(
            (tab) => {
                const tabNames = {
                    [ACTIVITY_DETAIL_TABS.MAP]: 'mapWidget',
                    images: 'imagesWidget',
                    [ACTIVITY_DETAIL_TABS.ATTACHMENTS]: 'documentsWidget',
                    [ACTIVITY_DETAIL_TABS.EXTRA_INFO]: 'extraFieldsWidget',
                    [ACTIVITY_DETAIL_TABS.EMAIL]: 'emailWidget',
                    [ACTIVITY_DETAIL_TABS.TOTAL_VIEWS]: 'openedEmails',
                    [ACTIVITY_DETAIL_TABS.TOTAL_LINKS]: 'clickedLinks',
                };

                if (tabNames[tab]) {
                    logEvent({ event: ACTIVITIES.trueName, submodule: tabNames[tab] });
                }

                if (tab === ACTIVITY_DETAIL_TABS.TIMELINE) {
                    logEvent({
                        event: ACTIVITIES.trueName,
                        submodule: 'timelineConversations',
                        functionality: 'detailView',
                    });
                }

                if (isFromWidgetDetail && onChangeTabForWidget) {
                    onChangeTabForWidget(tab);
                }
                setTab(tab);
            },
            [isFromWidgetDetail, onChangeTabForWidget, setTab],
        );

        const tabsClassName = classnames('fm-activities__detail-tabs-container', {
            'is-from-widget': isFromWidgetDetail,
        });

        // --- Memoized props
        const detailFilesProps = useMemo(
            () => ({
                canDeleteImages,
                canUploadDocuments: canCreateDocuments,
                canUploadImages,
                emptyDocumentsDesc: 'label_empty_documents_activities_desc',
                emptyDocumentsDescPermission:
                    'label_empty_documents_activities_not_permission_desc',
                emptyDocumentsTitle: 'label_empty_documents_activities_title',
                emptyDocumentsTitlePermission:
                    'label_empty_documents_activities_not_permission_title',
                emptyImagesDesc: 'label_empty_attachments_activities_desc',
                emptyImagesDescPermission: 'label_empty_attachments_activities_not_permission_desc',
                emptyImagesTitle: 'label_empty_attachments_activities_title',
                emptyImagesTitlePermission:
                    'label_empty_attachments_activities_not_permission_title',
                entity: ACTIVITIES,
                entityId: activity?.Id,
                getFiles: getActivityFiles,
                imagesFileGroupLabel: 'label_images_with_total',
                removeFile: removeActivityFile,
                showDocuments: true,
                showImages: true,
            }),
            [
                activity?.Id,
                canCreateDocuments,
                canDeleteImages,
                canUploadImages,
                getActivityFiles,
                removeActivityFile,
            ],
        );

        const detailEmailProps = useMemo(
            () => ({
                activity,
                emailWithImages,
                getEmailWithImages,
                labels,
                loadingEmailWithImages,
            }),
            [activity, emailWithImages, getEmailWithImages, labels, loadingEmailWithImages],
        );

        const detailWhatsappProps = useMemo(
            () => ({
                date: getDateText(activity?.ActivityDate),
                labels,
                message: activity?.WhatsappMessage,
                owner: activity?.ActivityOwner,
            }),
            [
                activity?.ActivityDate,
                activity?.ActivityOwner,
                activity?.WhatsappMessage,
                getDateText,
                labels,
            ],
        );

        const tabsProps = useMemo(
            () => ({
                activeKey: tab,
                className: 'fm-activities__detail-tabs',
                onChange: onChangeTab,
                tabs,
            }),
            [onChangeTab, tab, tabs],
        );

        // --- Render
        return (
            <div className="fm-activities__detail" style={{ width: finalWidth }}>
                {!activity && !loading && !forceLoader && (
                    <CustomEmptyView emptyViewProps={noDetailProps} />
                )}
                {(activity || loading || forceLoader) && (
                    <div className="fm-activities__detail-info" ref={domDetailInfo}>
                        {(loading || forceLoader) && (
                            <DetailLoader
                                width={domDetailInfo.current?.offsetWidth || defaultLoaderWidth}
                                height={defaultLoaderHeight}
                            />
                        )}
                        {activity && !loading && !forceLoader && (
                            <GeneralDetail {...generalDetailProps} />
                        )}
                    </div>
                )}
                {activity && !loading && !forceLoader && tab && tabs.length > 0 && (
                    <Fragment>
                        <div className="fm-activities__detail-divider" />
                        <Tabs {...tabsProps} />
                        <div className={tabsClassName} ref={domDetailTabs}>
                            {tab === ACTIVITY_DETAIL_TABS.INFORMATION &&
                                activity?.ActivityType === ACTIVITY_TYPE_MARKETING_CAMPAIGN &&
                                activity.Campaign && (
                                    <DetailMarketingCampaign
                                        activity={activity}
                                        isFromWidgetDetail={isFromWidgetDetail}
                                    />
                                )}
                            {tab === ACTIVITY_DETAIL_TABS.INTERACTIONS &&
                                activity?.ActivityType === ACTIVITY_TYPE_MARKETING_CAMPAIGN && (
                                    <DetailMarketingCampaignInteractions activity={activity} />
                                )}
                            {tab === ACTIVITY_DETAIL_TABS.EXTRA_INFO && (
                                <DetailExtraFields
                                    width={domDetailTabs.current?.offsetWidth}
                                    height={domDetailTabs.current?.offsetHeight}
                                    activityId={activity.Id}
                                />
                            )}
                            {tab === ACTIVITY_DETAIL_TABS.MAP && <DetailMap activity={activity} />}
                            {tab === ACTIVITY_DETAIL_TABS.ATTACHMENTS && (
                                <DetailDocument activity={activity} />
                            )}
                            {tab === ACTIVITY_DETAIL_TABS.FILES && (
                                <DetailFiles {...detailFilesProps} />
                            )}
                            {tab === ACTIVITY_DETAIL_TABS.EMAIL && (
                                <DetailEmail {...detailEmailProps} />
                            )}
                            {tab === ACTIVITY_DETAIL_TABS.WHATSAPP && (
                                <DetailWhatsapp {...detailWhatsappProps} />
                            )}
                            {tab === ACTIVITY_DETAIL_TABS.TIMELINE && (
                                <DetailMessages
                                    activity={activity}
                                    handleNewMessage={handleNewMessage}
                                    initialTabItem={initialTabItem}
                                    isOpen={true}
                                    matchingName={matchingName}
                                    scrollToMessage={scrollToMessage}
                                    setForceTotalMessages={setForceTotalMessages}
                                    setScrollToMessage={setScrollToMessage}
                                    shouldForceFocus={false}
                                    storedMessage={newMessagesRef.current[newMessageUID]}
                                />
                            )}
                            {tab === ACTIVITY_DETAIL_TABS.TOTAL_VIEWS && (
                                <DetailTotalViews trackingInfo={activity?.Tracking} />
                            )}
                            {tab === ACTIVITY_DETAIL_TABS.TOTAL_LINKS && (
                                <DetailTotalLinks trackingInfo={activity?.Tracking} />
                            )}
                        </div>
                    </Fragment>
                )}
            </div>
        );
    },
);

ActivitiesDetail.displayName = 'ActivitiesDetail';

export default ActivitiesDetail;
